import { PatientId, useCurrentPatientId } from '@/modules/surgicalEvent';
import { useErrorDialog, useSnackbar } from '@ianesthesie-kit/redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SentryService } from '@ianesthesie-kit/sentry';
import {
  secondsToMillis,
  SetTimeout,
  useTranslator
} from '@ianesthesie-kit/core';
import { minutesToMilliseconds } from 'date-fns';
import {
  epicSlice,
  useEpicDocumentsQuery,
  useEpicSurgicalEventQuery,
  useManualEpicLinkMutation,
  useSyncEpicDataMutation
} from './epic.slice';
import {
  EpicDocument,
  EpicPatientSyncPayload,
  EpicSurgicalEvent
} from './epic.model';
import { ID } from '@ianesthesie-kit/services';
import { useCurrentPatientIdParam, useQueryForCurrentPatient } from '@/hooks';
import { themeColors } from '@ianesthesie-kit/config';
import { useIsHospitalModuleEnabled } from '@/modules/appContext';

export const useIsCurrentHospitalEpicEnabled = () =>
  useIsHospitalModuleEnabled('epic');

export const useCurrentEpicSurgicalEvent = (config?: { skip?: boolean }) => {
  const epicEnabled = useIsCurrentHospitalEpicEnabled();
  return useQueryForCurrentPatient<EpicSurgicalEvent | null>({
    useQuery: useEpicSurgicalEventQuery,
    queryConfig: {
      skip: !epicEnabled || config?.skip
    }
  });
};

export const useCurrentIsLinkedToEpic = () =>
  !!useCurrentEpicSurgicalEvent().currentData;

export const useCurrentEpicDocuments = () => {
  const epicLinked = !!useCurrentEpicSurgicalEvent().currentData;
  return useQueryForCurrentPatient<EpicDocument[]>({
    useQuery: useEpicDocumentsQuery,
    queryConfig: {
      skip: !epicLinked,
      pollingInterval: minutesToMilliseconds(10)
    }
  });
};

const { useEpicDocumentBase64DataQuery } = epicSlice;

export const useCurrentEpicDocumentDataHtml = (epicDoc?: EpicDocument) =>
  useEpicDocumentDataHtml(useCurrentPatientIdParam(), epicDoc);

export const useEpicDocumentDataHtml = (
  patientId: ID = '',
  epicDoc?: EpicDocument,
  skip = false
) => {
  const { showErrorDialog } = useErrorDialog();
  const docId = epicDoc?.id;
  const { currentData: base64Data, isFetching } =
    useEpicDocumentBase64DataQuery(
      { patientId: patientId, documentId: docId ?? '' },
      {
        skip: skip || !docId || !patientId
      }
    );

  const [safeHtml, setSafeHtml] = useState('');
  const [emphasizedSafeHtml, setEmphasizedSafeHtml] = useState('');
  const [convertingToHtml, setConvertingToHtml] = useState(false);
  const [emphasizingHtml, setEmphasizingHtml] = useState(false);

  useEffect(() => {
    const contentType = epicDoc?.contentType;
    if (!contentType || !base64Data) {
      setSafeHtml('');
      setConvertingToHtml(false);
    } else {
      setConvertingToHtml(true);
      const currentId = docId;
      fetch('/api/toHtml', {
        method: 'POST',
        body: JSON.stringify({ contentBase64: base64Data, contentType })
      })
        .then((r) => r.json())
        .then(async ({ html }) => {
          if (docId === currentId) {
            const domPurify = await import('dompurify').then(
              (mod) => mod.default
            );
            setSafeHtml(domPurify.sanitize(html));
          }
        })
        .catch((err) => {
          showErrorDialog(err);
          setSafeHtml('');
          SentryService.getInstance().captureException(err);
        })
        .finally(() => {
          if (docId === currentId) {
            setConvertingToHtml(false);
          }
        });
    }
  }, [base64Data, epicDoc, docId, showErrorDialog]);

  const loading = convertingToHtml || isFetching || emphasizingHtml;

  const lastProcessedId = useRef<ID>(undefined);
  useEffect(() => {
    if (
      convertingToHtml ||
      !safeHtml ||
      !docId ||
      lastProcessedId.current === docId
    ) {
      setEmphasizedSafeHtml('');
      setEmphasizingHtml(false);
      return;
    }

    lastProcessedId.current = docId;
    if (!epicDoc?.redFlags?.length) {
      return;
    }

    let mounted = true;
    setEmphasizingHtml(true);
    emphasizeRedFlags(safeHtml, epicDoc.redFlags)
      .then((html) => {
        if (mounted) {
          setEmphasizedSafeHtml(html);
        }
      })
      .catch((err) => {
        showErrorDialog(err);
        setEmphasizedSafeHtml('');
        SentryService.getInstance().captureException(err);
      })
      .finally(() => {
        setEmphasizingHtml(false);
      });

    return () => {
      mounted = false;
    };
  }, [convertingToHtml, docId, safeHtml, showErrorDialog, epicDoc?.redFlags]);

  return useMemo(
    () => ({
      isLoading: loading,
      safeHtml: emphasizedSafeHtml
    }),
    [loading, emphasizedSafeHtml]
  );
};

export const useCurrentLinkEpic = () => {
  const { patientId } = useCurrentPatientId();
  const [link, ...mutationProps] = useManualEpicLinkMutation();

  const handleLink = useCallback(
    (payload: Omit<EpicPatientSyncPayload, 'patientId'>) =>
      link({ patientId, ...payload }).unwrap(),
    [link, patientId]
  );

  return [handleLink, ...mutationProps] as const;
};

export const useCurrentSyncEpicData = () => {
  const patientId = useCurrentPatientId();
  const [sync, ...mutationProps] = useSyncEpicDataMutation();
  const { startEpicDocumentsInterval } = useRefreshEpicDocuments(patientId);

  const handleSync = useCallback(
    () =>
      sync(patientId)
        .unwrap()
        .then(async (resp) => {
          await startEpicDocumentsInterval();
          return resp;
        }),
    [sync, patientId, startEpicDocumentsInterval]
  );

  return [handleSync, ...mutationProps] as const;
};

const { useLazyEpicDocumentsQuery } = epicSlice;
const REFRESH_INTERVAL_MS = secondsToMillis(15);
const REFRESH_INTERVAL_MAX_TIME_MS = minutesToMilliseconds(5);

export const useRefreshEpicDocuments = (
  patient: PatientId | ID | undefined
) => {
  const { showSnackbar } = useSnackbar();
  const translate = useTranslator();
  const patientId = !patient
    ? undefined
    : typeof patient === 'object'
    ? patient.patientId
    : patient;
  const [refetch, { currentData: epicDocuments }] = useLazyEpicDocumentsQuery();
  const interval = useRef<SetTimeout>(undefined);
  const intervalTimeLimit = useRef<number>(undefined);
  const documentsReceived = useRef(false);
  const firstDocId = useRef<ID>(undefined);

  useEffect(() => {
    if (epicDocuments?.length) {
      const currentFirstId = epicDocuments[0].id;
      if (!firstDocId.current) {
        firstDocId.current = currentFirstId;
      }

      if (firstDocId.current !== currentFirstId) {
        documentsReceived.current = true;
        showSnackbar({
          severity: 'info',
          title: translate(
            'doctor-dashboard.epic.documentsImport.successSnack.title'
          ),
          message: '',
          position: {
            horizontal: 'left',
            vertical: 'bottom'
          }
        });
      }
    }
  }, [epicDocuments, showSnackbar, translate]);

  return useMemo(
    () => ({
      startEpicDocumentsInterval: () => {
        if (!patientId) {
          return Promise.reject(new Error('No patient id provided'));
        }
        clearInterval(interval.current);
        documentsReceived.current = false;
        intervalTimeLimit.current = Date.now() + REFRESH_INTERVAL_MAX_TIME_MS;
        interval.current = setInterval(() => {
          if (
            Date.now() >= (intervalTimeLimit.current ?? 0) ||
            documentsReceived.current
          ) {
            clearInterval(interval.current);
            if (!documentsReceived.current) {
              showSnackbar({
                severity: 'error',
                title: translate(
                  'doctor-dashboard.epic.documentsImport.failureSnack.title'
                ),
                message: translate(
                  'doctor-dashboard.epic.documentsImport.failureSnack.body'
                ),
                position: {
                  horizontal: 'left',
                  vertical: 'bottom'
                }
              });
            }
          } else {
            refetch({ patientId });
          }
        }, REFRESH_INTERVAL_MS);

        return Promise.resolve();
      }
    }),
    [patientId, refetch, showSnackbar, translate]
  );
};

async function emphasizeRedFlags(
  html: string,
  redFlags: string[]
): Promise<string> {
  // Parse the HTML to work with DOM nodes
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Function to wrap text in emphasis span
  const wrapInEmphasis = (text: string) => {
    let result = text;
    redFlags.forEach((term) => {
      const regex = new RegExp(`\\b(${term})\\b`, 'gi');
      result = result.replace(
        regex,
        `<span style="color: ${themeColors.red[500]}!important;font-weight: 600!important;">$1</span>`
      );
    });
    return result;
  };

  // Walk through all text nodes only
  const walkTextNodes = (node: Element) => {
    if (node.nodeType === Node.TEXT_NODE) {
      // Only process if the text contains red flags
      const hasRedFlag = redFlags.some((term) =>
        new RegExp(`\\b${term}\\b`, 'i').test(node.textContent)
      );

      if (hasRedFlag && node.textContent.trim()) {
        // Create a temporary container
        const temp = document.createElement('div');
        temp.innerHTML = wrapInEmphasis(node.textContent);

        // Replace the text node with the new content
        while (temp.firstChild) {
          node.parentNode?.insertBefore(temp.firstChild, node);
        }
        node.parentNode?.removeChild(node);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Skip script and style elements
      if (node.tagName !== 'SCRIPT' && node.tagName !== 'STYLE') {
        // Process child nodes (convert to array first to avoid live collection issues)
        Array.from(node.childNodes).forEach((child) =>
          walkTextNodes(child as Element)
        );
      }
    }
  };

  walkTextNodes(doc.body);

  // Return the modified HTML
  return doc.body.innerHTML;
}
